/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IProductDimensionsWithAvailabilitiesFull } from '@msdyn365-commerce/commerce-entities';
import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, IAny, ICreateActionContext, IGeneric } from '@msdyn365-commerce/core';
import { ProductDimensionAvailabilitySearchCriteria, ProductsDataActions
} from '@msdyn365-commerce/retail-proxy';

import { ArrayExtensions, getSelectedProductIdFromActionInput } from './utilities';

/**
 * Product dimension with availability Input.
 */
export class ProductDimensionsWithAvailabilitiesInput implements IActionInput {

    public productId: number;

    public channelId: number;

    public constructor(productId: number, channelId: number) {
        this.productId = productId;
        this.channelId = channelId;
    }

    /**
     * Cache key.
     * @returns String.
     */
    public getCacheKey: () => string = () => 'ProductDimensionsWithAvailabilities';

    /**
     * Cache object type.
     * @returns String.
     */
    public getCacheObjectType: () => string = () => 'ProductDimensionsWithAvailabilities';

    /**
     * Cache type.
     * @returns String.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * Create input method for the get-product-dimension-with-availability data action.
 * @param inputData - The input data passed to the createInput method.
 * @returns - The input for the data action.
 */
export const createProductDimensionsWithAvailabilitiesInput = (inputData: ICreateActionContext<IGeneric<IAny>>): ProductDimensionsWithAvailabilitiesInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new ProductDimensionsWithAvailabilitiesInput(+productId, +inputData.requestContext.apiSettings.channelId);
    }
    throw new Error('Unable to create ProductDimensionsWithAvailabilitiesInput, no productId found on module config or query');
};

/**
 * Action method for the getProductDimensionsWithAvailabilities Data Action.
 * @param input - Input for the data action.
 * @param context - Action context.
 * @returns Product dimensions with availabilities.
 */
export async function getProductDimensionsWithAvailabilitiesAction(
    input: ProductDimensionsWithAvailabilitiesInput,
    context: IActionContext
): Promise<IProductDimensionsWithAvailabilitiesFull[]> {
    const activeProduct = await ProductsDataActions.getByIdAsync({ callerContext: context }, input.productId, input.channelId);

    if (!ArrayExtensions.hasElements(activeProduct.Dimensions)) {
        return [];
    }

    const promises = activeProduct.Dimensions.map(dimension => {
        const searchCriteria: ProductDimensionAvailabilitySearchCriteria =
        {
            RequestedDimensionTypeValue: dimension.DimensionTypeValue,
            MatchingDimensionValues: [],
            DefaultWarehouseOnly: false,
            FilterByChannelFulfillmentGroup: true,
            DeliveryModeTypeFilterValue: 0
        };

        const dimensionValuesPromise = ProductsDataActions.getDimensionValuesWithEstimatedAvailabilitiesAsync(
            { callerContext: context, queryResultSettings: {} },
            // eslint-disable-next-line inclusive-language/use-inclusive-words -- notation used in commerce
            activeProduct.MasterProductId ? activeProduct.MasterProductId : activeProduct.RecordId,
            searchCriteria
        );
        // eslint-disable-next-line promise/prefer-await-to-then -- Existing pattern to execute promises
        const fullDimensionPromise = dimensionValuesPromise.then(dimensionValues => {
            const fullDimension: IProductDimensionsWithAvailabilitiesFull = {
                ...dimension,
                dimensionValuesWithInventory: dimensionValues
            };
            return fullDimension;
        });

        return fullDimensionPromise;
    });

    return await Promise.all(promises);
}

/**
 * The GetProductDimensionsWithAvailabilitiesAction
 * Gets the available product dimensions for the product via the getDimensionValueswithEstimatedAvailibilities RetailServer API.
 */
export const getProductDimensionsWithAvailabilities = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-product-dimensions-with-availabilities',
    action: getProductDimensionsWithAvailabilitiesAction as IAction<IProductDimensionsWithAvailabilitiesFull[]>,
    input: createProductDimensionsWithAvailabilitiesInput
});

export default getProductDimensionsWithAvailabilities;
